import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { GlassCard } from '../../components/GlassCard';
import { FileText, Eye, Plus, ArrowDownLeft, ArrowUpRight, ArrowLeftRight } from 'lucide-react';

const PAGE_SIZE = 25;
const TYPE_TABS = [
  { key: '', label: 'All' },
  { key: 'payment', label: 'Payment' },
  { key: 'receipt', label: 'Receipt' },
  { key: 'contra', label: 'Contra' },
];
const TYPE_COLORS = {
  payment: 'bg-red-50 text-red-600 border-red-200',
  receipt: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  contra: 'bg-blue-50 text-blue-600 border-blue-200',
};
const TYPE_ICONS = { payment: ArrowDownLeft, receipt: ArrowUpRight, contra: ArrowLeftRight };

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function VouchersList() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (typeFilter) params.type = typeFilter;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const r = await api.voucherList(token, params);
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      if (e.status !== 401) toast.show(e.message || 'Failed to load', 'error');
    } finally { setLoading(false); }
  }, [token, typeFilter, fromDate, toDate, page, toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = search.trim()
    ? items.filter(v => {
        const meta = v.voucher_meta || {};
        const text = `${meta.party_name || ''} ${v.description || ''} ${meta.reference_no || ''}`.toLowerCase();
        return text.includes(search.toLowerCase());
      })
    : items;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="vouchers-list-page">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-200 flex items-center justify-center">
            <FileText size={20} className="text-brand-purple" />
          </div>
          <h1 className="text-gray-900 font-extrabold text-xl">Vouchers</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/vouchers/payment" data-testid="new-payment-btn"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-red-50 text-red-600 text-sm font-bold border border-red-200 hover:bg-red-100 transition-colors">
            <ArrowDownLeft size={14} /> Payment
          </Link>
          <Link to="/vouchers/receipt" data-testid="new-receipt-btn"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-50 text-emerald-600 text-sm font-bold border border-emerald-200 hover:bg-emerald-100 transition-colors">
            <ArrowUpRight size={14} /> Receipt
          </Link>
          <Link to="/vouchers/contra" data-testid="new-contra-btn"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-50 text-blue-600 text-sm font-bold border border-blue-200 hover:bg-blue-100 transition-colors">
            <ArrowLeftRight size={14} /> Contra
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1" data-testid="voucher-type-tabs">
          {TYPE_TABS.map(t => (
            <button key={t.key} onClick={() => { setTypeFilter(t.key); setPage(1); }}
              data-testid={`vtab-${t.key || 'all'}`}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition-all ${typeFilter === t.key ? 'bg-brand-purple/10 border-brand-purple text-brand-purple' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <input type="text" placeholder="Search party / narration..." value={search} onChange={e => setSearch(e.target.value)}
          data-testid="voucher-search"
          className="h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm w-56 focus:border-brand-purple outline-none" />
        <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }} data-testid="voucher-from-date"
          className="h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
        <span className="text-gray-400 text-xs">to</span>
        <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }} data-testid="voucher-to-date"
          className="h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
        <span className="text-gray-400 text-xs ml-auto">{total} voucher{total !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <GlassCard className="overflow-hidden" data-testid="vouchers-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vouchers-table">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Date</th>
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Type</th>
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Party</th>
                <th className="text-right px-5 py-3 text-gray-500 text-xs font-bold uppercase">Amount</th>
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Mode</th>
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Reference</th>
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Narration</th>
                <th className="text-right px-5 py-3 text-gray-500 text-xs font-bold uppercase w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    {Array.from({ length: 8 }).map((_, j) => <td key={j} className="px-5 py-3"><div className="h-4 w-16 bg-gray-100 rounded animate-pulse" /></td>)}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center">
                    <FileText size={36} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm mb-2">No vouchers yet</p>
                    <Link to="/vouchers/payment" className="text-brand-purple text-sm font-bold hover:underline">Create your first voucher</Link>
                  </td>
                </tr>
              ) : filtered.map(v => {
                const meta = v.voucher_meta || {};
                const vt = v.voucher_type || 'payment';
                const Icon = TYPE_ICONS[vt] || FileText;
                return (
                  <tr key={v.entry_id} data-testid={`vrow-${v.entry_id}`}
                    className="border-b border-gray-100 hover:bg-gray-50/60 cursor-pointer transition-colors"
                    onClick={() => navigate(`/vouchers/${v.entry_id}`)}>
                    <td className="px-5 py-3 text-gray-900 font-semibold whitespace-nowrap">{fmtDate(v.entry_date)}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase border ${TYPE_COLORS[vt] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                        <Icon size={10} /> {vt}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-gray-700">{meta.party_name || '—'}</td>
                    <td className="px-5 py-3 text-right text-gray-900 font-bold tabular-nums">{formatMoney(meta.amount || v.debit_total)}</td>
                    <td className="px-5 py-3 text-gray-500 capitalize text-xs font-semibold">{meta.mode || '—'}</td>
                    <td className="px-5 py-3 text-gray-500 text-xs">{meta.reference_no || '—'}</td>
                    <td className="px-5 py-3 text-gray-500 text-xs max-w-[200px] truncate">{v.description || '—'}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={e => { e.stopPropagation(); navigate(`/vouchers/${v.entry_id}`); }}
                        data-testid={`vview-${v.entry_id}`}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <span className="text-gray-500 text-sm">{total} voucher{total !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">Prev</button>
            <span className="px-3 py-1.5 text-sm text-gray-500">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
